<template>
  <monitor-video
    :stoppable="is_event && data.view != 'source'"
    :icon-color="icon_color"
    :color="color"
    :urls="urls"
    :progress-percent="progress_percent"
    :progress-warning="is_near_to_end"
    :show-player="show_player"
    :player-text="player_text"
    :edit="true"
    :edit-text="is_event ? 'Edit event' : 'Edit channel'"
    :color-key="color_key"
    :auto-start="!!autoStart"
    lufs-button
    diamond-button
    frame-monitor-button
    @close="close"
    @edit="open_editor"
  >
    <template #title>
      <template v-if="is_event">{{event_title}}</template>
      <channel-title v-else :channel="data.channel" :alternative="data.alternative"></channel-title>
    </template>
    <template #subtitle>
      {{subtitle}}
    </template>
    <template #stop>
      <stop-event :uuid="data.event.uuid">
        <k-btn style="height: 15px; margin:0px; padding:0px;" size="m" icon color="danger" icon-name="stop"></k-btn>
      </stop-event>
    </template>
  </monitor-video>
</template>

<script>
import datelib from '@/lib/date.js';
import ChannelModel from '@/models/channel.js';
import EventModel from '@/models/event.js';
import InstanceModel from '@/models/instance.js';
import { MONITOR2_EVENT_VIEWS, monitor2ColorKey, toggleMonitor2Items } from '@/models/monitor.js';
import ChannelTitle from '@/components/ChannelTitle.vue';
import MonitorVideo from '@/components/MonitorVideo.vue';
import StopEvent from '@/components/StopEvent.vue';

export default {
  // data: {id, type: "channel", channel, alternative} or {id, type: "event", view, event}
  props: ['data', 'timestamp', 'autoStart'],
  components: {
    ChannelTitle,
    MonitorVideo,
    StopEvent
  },
  computed: {
    is_event () {
      return this.data.type == "event";
    },
    color_key () {
      return monitor2ColorKey(this.data.id);
    },
    view_title () {
      var view = _.find(MONITOR2_EVENT_VIEWS, {value: this.data.view});
      return view ? view.title : "";
    },
    // Channel whose headend preview is shown (channel tile or event source)
    source_channel () {
      if (!this.is_event) return this.data.channel;
      if (this.data.view == "source") return EventModel.input_channel(this.data.event);
      return undefined;
    },
    source_alternative () {
      if (!this.is_event) return this.data.alternative;
      return EventModel.alternative(this.data.event);
    },
    source_status () {
      if (!this.source_channel) return undefined;
      return ChannelModel.status(this.source_channel, this.source_alternative);
    },
    source_state () {
      return _.get(this.source_status, "instance.source.state", "");
    },
    source_url () {
      if (!this.source_status) return "";
      var headend = this.$store.state.instances[this.source_status.headend_instance];
      if (typeof(headend) == "undefined") return "";
      return InstanceModel.headend_preview_url(headend, this.source_channel.uuid, this.source_alternative) || "";
    },
    url () {
      if (!this.is_event || this.data.view == "source") return this.source_url;
      if (this.data.view == "preview") return EventModel.preview_url(this.data.event);
      if (this.data.view == "live") return EventModel.live_url(this.data.event);
      return "";
    },
    urls () {
      return [{title: this.is_event ? this.view_title : "Monitor", url: this.url}];
    },
    show_player () {
      if (!this.is_event || this.data.view == "source") return !!this.url && this.source_state == "connected";
      return !!this.url && (this.is_running || this.is_future);
    },
    player_text () {
      if (this.is_event) {
        if (this.is_past) return "Event has ended";
        if (this.data.view != "source" && this.is_future) return "Event has not yet started";
      }
      return "No preview";
    },
    state_color () {
      if (this.source_state == "listening") return "primary";
      if (this.source_state == "disconnected") return "danger";
      if (this.source_state == "connecting") return "warning";
      if (this.source_state == "connected") return "success";
      return undefined;
    },
    event_color () {
      if (this.is_running) return "success";
      if (this.is_future) return "info";
      if (this.is_past) return "past";
      return "primary";
    },
    color () {
      return this.is_event ? this.event_color : undefined;
    },
    icon_color () {
      if (this.is_event && this.data.view != "source") return this.event_color;
      return this.state_color;
    },
    progress_percent () {
      return this.is_event ? EventModel.progress_percent(this.data.event, this.timestamp) : 0;
    },
    is_past () {
      return this.is_event && EventModel.is_past(this.data.event, this.timestamp);
    },
    is_running () {
      return this.is_event && EventModel.is_current(this.data.event, this.timestamp);
    },
    is_future () {
      return this.is_event && EventModel.is_future(this.data.event, this.timestamp);
    },
    is_near_to_end () {
      return this.is_event && EventModel.is_near_to_end(this.data.event, this.timestamp);
    },
    event_title () {
      return EventModel.title(this.data.event);
    },
    subtitle () {
      if (!this.is_event) return this.source_state;
      var time = datelib.format_date(this.data.event.cached_start_time) + " - " + datelib.format_time(this.data.event.cached_start_time);
      if (this.data.view == "source") return this.view_title + (this.source_state ? " (" + this.source_state + ")" : "") + " · " + time;
      return this.view_title + " · " + time;
    }
  },
  methods: {
    open_editor () {
      if (this.is_event) this.$router.push("/monitor2/event/" + this.data.event.uuid);
      else this.$router.push("/monitor2/channel/" + this.data.channel.uuid);
    },
    close () {
      if (!this.$store.state.account) return;
      var acc = _.cloneDeep(this.$store.state.account);
      this.$store.dispatch("update_profile", toggleMonitor2Items(acc, [this.data.id], false));
    }
  }
}
</script>
